/**
 * Extensions personalize.py's `_download_brand_logo` may have saved the
 * client's logo under, and the MIME type each maps to (for the favicon
 * `<link>`'s `type` attribute).
 *
 * png/jpg first, svg last: the template always ships a default
 * `public/brand/logo.svg`, and personalize.py's `_download_brand_logo`
 * never removes it when it saves a non-svg logo alongside it. An svg-first
 * probe would therefore always find that leftover default and never reach
 * a real png/jpg client logo. Trying the raster formats first costs two
 * failed same-origin requests on an unbranded scaffold (there's no
 * logo.png/.jpg to find), which is a fine trade for actually showing a
 * branded client's logo. The full fix -- personalize.py clearing sibling
 * `logo.*` files before writing the new one -- is a scaffold-ui change,
 * out of this repo's scope.
 */
const LOGO_CANDIDATES: { ext: string; type: string }[] = [
  { ext: "png", type: "image/png" },
  { ext: "jpg", type: "image/jpeg" },
  { ext: "svg", type: "image/svg+xml" },
];

export const LOGO_EXTENSIONS = LOGO_CANDIDATES.map((c) => c.ext);

/**
 * Upgrades the `<link rel="icon">` favicon to whichever brand logo file
 * actually loads, since the static tag in index.html.jinja can only point
 * at one fixed path and `type`. Best-effort and silent: probes each
 * candidate off-DOM and only swaps the href/type on success, so any
 * failure just leaves the static default in place.
 */
export function upgradeFavicon(): void {
  const link = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
  if (!link) {
    return;
  }

  const tryFrom = (index: number) => {
    const candidateSpec = LOGO_CANDIDATES[index];
    if (!candidateSpec) {
      return;
    }
    const { ext, type } = candidateSpec;
    const candidate = `/brand/logo.${ext}`;
    const probe = new Image();
    probe.onload = () => {
      link.href = candidate;
      link.type = type;
    };
    probe.onerror = () => tryFrom(index + 1);
    probe.src = candidate;
  };
  tryFrom(0);
}
